import { useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconMailOpened } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import type { MailIntakeSetupIssue } from '../lib/api/domains/mailIntake';

const DISMISS_KEY = 'mail-intake-setup-banner-dismissed';

const TITLE: Record<MailIntakeSetupIssue['step'], string> = {
  MICROSOFT: 'Mail-Eingang: Microsoft-App fehlt',
  MAILBOX: 'Mail-Eingang noch nicht eingerichtet',
  INACTIVE: 'Mail-Eingang ist eingerichtet, aber nicht aktiv',
  ERROR: 'Mail-Eingang: Abruf schlägt fehl',
};

/**
 * Admin-Hinweis: Mail-Eingang (Schattenbetrieb) einrichten bzw. reparieren.
 *
 * Nur ADMIN — die Einstellung und die Auswertung zeigen Kundenmails quer über alle Kunden.
 * Was fehlt, sagt der Server (`mailIntakeSetupIssue`), derselbe Text wie im Reiter. Führt per
 * Deeplink in Einstellungen → Mail-Eingang. Pro Browser-Session wegklickbar; eingerichtet und
 * fehlerfrei verschwindet er von selbst.
 */
export function MailIntakeSetupBanner() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const { data } = useQuery({
    queryKey: ['mail-intake', 'setup-status'],
    queryFn: () => api.getMailIntakeSetupStatus(),
    enabled: isAdmin && !dismissed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const issue = data?.setupIssue;
  if (!isAdmin || dismissed || !issue) return null;
  const isError = issue.step === 'ERROR';

  return (
    <Alert
      color={isError ? 'red' : 'orange'}
      radius="md"
      mb="md"
      icon={<IconMailOpened size={18} />}
      withCloseButton
      onClose={() => {
        try {
          sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
          // ohne sessionStorage kommt der Hinweis beim nächsten Laden eben wieder
        }
        setDismissed(true);
      }}
      title={TITLE[issue.step]}
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="sm" maw={620}>
          {issue.message}{' '}
          {isError
            ? 'Der Schattenbetrieb liest gerade keine Mails mit.'
            : 'Der ServiceHub soll das Support-Postfach mitlesen und zeigen, was er mit jeder Mail täte — im Schattenbetrieb, Tickets und Mails macht weiter weclapp.'}
        </Text>
        <Button size="xs" color={isError ? 'red' : 'orange'} onClick={() => navigate('/admin/settings?tab=mail_intake')}>
          {isError ? 'Ansehen' : 'Jetzt einrichten'}
        </Button>
      </Group>
    </Alert>
  );
}
